/**
 * GateCampaignProgress Component
 *
 * Displays campaign progress information in the wallet dropdown:
 * - "Earned X/20,000 Points" progress
 * - Campaign end date and countdown
 *
 * Only visible when user is using Gate wallet.
 * Uses Gate's official branding (blue/green gradient).
 */

import { useMemo } from 'react';

import config from '@/config';
import { useGateCampaign } from '@/hooks/useGateCampaign';

// Gate brand colors
const GATE_GREEN = '#17E6A1';
const GATE_BLUE = '#2354E6';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  return `${hours}h left`;
}

export function GateCampaignProgress() {
  const { campaignData, isLoading } = useGateCampaign();

  const timeRemaining = useMemo(() => {
    if (!campaignData?.endDate) return '';
    return formatTimeRemaining(campaignData.endDate);
  }, [campaignData?.endDate]);

  // Feature flag check
  if (!config.ENABLE_GATE_WALLET_CAMPAIGN) {
    return null;
  }

  // Don't show if loading or campaign not active or user is not using Gate wallet
  if (isLoading || !campaignData?.isActive || !campaignData?.isEligible) {
    return null;
  }

  const progressPercent = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  return (
    <div className="px-4 py-2">
      <div
        className="flex flex-col gap-2 px-3 py-2 rounded-lg text-xs"
        style={{
          backgroundColor: 'rgba(23, 230, 161, 0.08)',
          border: `1px solid rgba(23, 230, 161, 0.3)`,
          fontFamily: 'Outfit, sans-serif',
        }}
      >
        {/* Header with icon */}
        <div className="flex items-center gap-2">
          <div className="rounded px-1 py-0.5" style={{ background: 'rgba(240, 240, 240, 0.95)' }}>
            <img src="/gate-color-short.png" alt="Gate" className="h-3" />
          </div>
          <span
            className="font-semibold"
            style={{
              background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            2x Bonus
          </span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: 'rgba(23, 230, 161, 0.2)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.min(progressPercent, 100)}%`,
              background: `linear-gradient(90deg, ${GATE_BLUE}, ${GATE_GREEN})`,
            }}
          />
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between gap-2">
          <span style={{ color: '#999' }}>
            {campaignData.userBonusEarned.toLocaleString()}/{campaignData.maxBonus.toLocaleString()} pts
          </span>
          <span style={{ color: GATE_GREEN, fontWeight: 600 }}>
            {timeRemaining}
          </span>
        </div>
      </div>
    </div>
  );
}
